import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

interface GaugeChartProps {
  value: number;
  min?: number;
  max?: number;
  height?: number;
  label?: string;
  unit?: string;
  colorRanges?: {
    threshold: number;
    color: string;
  }[];
}

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  min = 0,
  max = 100,
  height = 200,
  label,
  unit = '',
  colorRanges = [
    { threshold: 30, color: '#ef4444' }, // Red
    { threshold: 60, color: '#f59e0b' }, // Orange
    { threshold: 100, color: '#10b981' }, // Green
  ],
}) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const angle = (percentage / 100) * 180;

  // Determine color based on percentage
  const getColor = () => {
    for (const range of colorRanges) {
      if (percentage <= range.threshold) {
        return range.color;
      }
    }
    return colorRanges[colorRanges.length - 1].color;
  };

  const color = getColor();

  // Create data for the gauge
  const data = [
    { name: 'value', value: angle },
    { name: 'remaining', value: 180 - angle },
  ];

  const RADIAN = Math.PI / 180;
  const needleLength = 60;
  const needleX = 100 + needleLength * Math.cos(-RADIAN * (90 + angle));
  const needleY = 100 + needleLength * Math.sin(-RADIAN * (90 + angle));

  return (
    <div className="relative flex flex-col items-center" style={{ height }}>
      <ResponsiveContainer width="100%" height={height * 0.8}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="80%"
            startAngle={180}
            endAngle={0}
            innerRadius={60}
            outerRadius={80}
            dataKey="value"
            stroke="none"
          >
            <Cell fill={color} />
            <Cell fill="var(--border-subtle)" opacity={0.3} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Needle */}
      <svg
        className="absolute top-0 left-1/2 transform -translate-x-1/2"
        width="200"
        height={height * 0.8}
        style={{ pointerEvents: 'none' }}
      >
        <line
          x1="100"
          y1="100"
          x2={needleX}
          y2={needleY}
          stroke="var(--text-primary)"
          strokeWidth="2"
        />
        <circle cx="100" cy="100" r="5" fill="var(--text-primary)" />
      </svg>

      {/* Value display */}
      <div className="absolute top-[45%] left-1/2 transform -translate-x-1/2 text-center">
        <div className="text-3xl font-bold text-foreground">
          {value}
          {unit && <span className="text-lg ml-1">{unit}</span>}
        </div>
        {label && (
          <div className="text-xs text-muted-foreground/70 mt-1">
            {label}
          </div>
        )}
      </div>

      {/* Min/Max labels */}
      <div className="flex justify-between w-full px-8 mt-2">
        <span className="text-xs text-muted-foreground/70">
          {min}
          {unit}
        </span>
        <span className="text-xs text-muted-foreground/70">
          {max}
          {unit}
        </span>
      </div>
    </div>
  );
};

